import React, { forwardRef, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import _ from 'lodash';
import { FieldTypes, parseSelectOptions } from 'utils/formUtils';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import { Form } from 'antd';

const ClaytonMappingForm = forwardRef(function ClaytonMappingForm({disableProjectFields = false, ...props}, ref) {
  const editModeRef = useRef(!_.isEmpty(props.record));
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [projectList, setProjectList] = useState(() => {
    if(props.record && !_.isNull(props.record.projectId)){
        const { accountId, projectId, projectName} = props.record;
        const projectArray = projectId.split(',') || [];
        const projectNameArray = projectName.split(',') || [];
        return {
            [accountId]: projectArray.map((id, index) => ({id, name: projectNameArray[index]}))
        }
    }
    return {}
  });

  const [accountId, setAccountId] = useState(() => {
    if(props.record){
        const { accountId } = props.record;
        return accountId
    };
    return ''
  });

  const [projectId, setProjectId] = useState(() => {
    if(props.record){
        const { projectId } = props.record;
        return projectId
    };
    return ''
  });

  const [branchList, setBranchList] = useState(() => {
    if(props.record && !_.isNull(props.record.branchName)){
        const { projectId, branchName} = props.record;
        return {
            [projectId]: [{id: branchName, name: branchName}]
        }
    }
    return {}
  });

  const [accounts, setAccounts] = useState(() => {
    if(props.record){
        const { accountId, accountName} = props.record;
        return [{id: accountId, name: accountName}]
    };
    return []
  });


 


  useEffect(() => {
    setLoading(true);
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });
    promise.then((response) => {
      const { projects } = response;
      setAccounts(projects)
    }).catch(() => {
      message.error('Problem fetching accounts');
    }).finally(() => setLoading(false));

    return cancel;
  }, [props.loginConfigId, props.toolName]);

  const fetchProjects = useCallback((accountId) => {
    if((editModeRef.current && accountId) || (accountId && !projectList[accountId])){
      editModeRef.current = false;
      const {promise,cancel} = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, 
        { login_id: props.loginConfigId, organisation: accountId});

        promise.then(response=>{
          const { projects } = response;
          setProjectList((projectList) =>({...projectList,[accountId]: projects}));
        }).catch(()=>{
        message.error('Problem fetching projects')
        })

        return cancel;
    }
  }, [projectList, props.loginConfigId, props.toolName]);

  const fetchBranches = useCallback((projectId) => {
    if((editModeRef.current && projectId) || (projectId && !branchList[projectId])){
      editModeRef.current = false;
      const {promise,cancel} = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, 
        { login_id: props.loginConfigId, organisation: accountId, project: projectId});

        promise.then(response=>{
          const { projects } = response;
          setBranchList((branchList) =>({...branchList,[projectId]: projects}));
        }).catch(()=>{
        message.error('Problem fetching projects')
        })

        return cancel;
    }
  }, [props.loginConfigId, props.toolName, accountId, branchList]);

  useEffect(() => fetchProjects(accountId), [accountId, fetchProjects]);

  useEffect(() => fetchBranches(projectId), [projectId, fetchBranches])

  
  const getExtraFieldsData = useCallback((config) => {
    const { accountId, projectId, branchName } = config; 

    return ({
        accountId,
        accountName: accounts.find(i => i.id === accountId).name,
        projectId,
        projectName: projectList[accountId].find(i => i.id === projectId).name,
        branchName
    });
  },[accounts, projectList]);


  const onAccountChange = useCallback((value, field) => {
    setAccountId(value);
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    let updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      projectId: undefined,
      branchName: undefined
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);

  const onProjectChange = useCallback((value, field) => {
    setProjectId(value);
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    let updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      branchName: undefined
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);

  const isProjectFieldDisabled = useCallback((fieldKey, form) => {
    const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
    return (!fieldConfiguration || !fieldConfiguration.accountId);
  }, []);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { projectId, accountId, branchName} = config;
    return ({
        accountId,
        projectId,
        branchName
    });
  }, []);


  const extraFields = useMemo(() => ([
    {
      name: 'accountId',
      colSpan: 12,
      type: FieldTypes.SELECT,
      label: 'Account Name',
      options: accounts.map(option => {
        const { label = option.name, value = option.id } = option;
        return { label, value }
      }),
      required: true,
      props: {
        autoFocus: true,
        onChange:  onAccountChange
      }
    },
    {
      name: 'projectId',
      colSpan: 12,
      type: FieldTypes.SELECT,
      label: 'Project Name',
      required: true,
      propsGenerator: (fieldKey) => ({ disabled: isProjectFieldDisabled(fieldKey, form)}),
      props: {
        getFormListOptions: (fieldKey) => {
          const configs = form.getFieldValue("configurations")
          const accountId = configs[fieldKey]?.accountId
          return accountId ? parseSelectOptions(projectList[accountId]): []
        },
        onChange: onProjectChange
      },
      },
      {
        name: 'branchName',
        colSpan: 12,
        type: FieldTypes.SELECT,
        label: 'Branch',
        required: true,
        propsGenerator: (fieldKey) => ({ disabled: isProjectFieldDisabled(fieldKey, form)}),
        props: {
          getFormListOptions: (fieldKey) => {
            const configs = form.getFieldValue("configurations")
            const projectId = configs[fieldKey]?.projectId
            return projectId ? parseSelectOptions(branchList[projectId])  : []
          }
        }
    }
  ]), [accounts, onAccountChange, projectList, form, isProjectFieldDisabled, onProjectChange, branchList]);


  return (
    <GenericMappingForm
      {...props}
      form={form}
      toolLoading={loading}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
    />
  )
})

export default ClaytonMappingForm;